import { FC, HTMLAttributes, RefObject, useEffect, useRef } from 'react';

import styles from './TickUp.module.css';

export interface TickUpProps extends HTMLAttributes<HTMLDivElement> {
  /** Start value */
  from: number;
  /** End value */
  to: number;
  /** TickUp duration in milliseconds */
  duration: number;
  /** Step between the values */
  step?: number;
  /** Classname styles provider */
  className?: string;
}

const updateCounter = (value: number, ref: RefObject<HTMLDivElement | null>): void => {
  if (ref.current) ref.current.textContent = value.toString();
};

/** TickUp component, renders animated counter from one number to another */
export const TickUp: FC<TickUpProps> = ({ from, to, step = 1, duration, className, ...rest }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const decimalsCount = step.toString().split('.')[1]?.length;

  useEffect(() => {
    rootRef.current && initTickUp();

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    };
  }, [from, to, step, duration, rootRef]);

  const initTickUp = (): void => {
    if (from === to) {
      if (rootRef.current) rootRef.current.textContent = to.toString();
      return;
    }

    let currentValue: number = from;
    const range: number = Math.abs(to - from);
    const finalValue: number = from > to ? Math.min(to, from) : Math.max(to, from);
    const increment: number = from < to ? step : -Math.abs(step);
    const tickUpSpeed: number = Math.floor(duration / (range / Math.abs(increment)));

    intervalRef.current = setInterval(() => {
      currentValue = decimalsCount
        ? Number((increment + currentValue).toFixed(decimalsCount))
        : increment + currentValue;

      if (increment > 0 && currentValue + increment >= finalValue) {
        updateCounter(finalValue, rootRef);
        clearInterval(intervalRef.current);
      } else if (increment < 0 && currentValue + increment <= finalValue) {
        updateCounter(finalValue, rootRef);
        clearInterval(intervalRef.current);
      } else {
        updateCounter(currentValue, rootRef);
      }
    }, tickUpSpeed);
  };

  return (
    <div className={`${styles.tickUp} ${className}`} ref={rootRef} {...rest}>
      {from}
    </div>
  );
};
